import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Search, Filter, Calendar, CheckCircle, AlertTriangle, XCircle, AlertCircle, Building2 } from 'lucide-react';
import { emailLogs, organizations } from '../services/api';
import VersionFooter from '../components/VersionFooter';

// "Emails enviados" — delivery status of every email GiA sends (metadata only;
// subjects and bodies are never stored). super_admin only.

const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
};

const STATUS = {
  sent: { label: 'Enviado', className: 'bg-blue-100 text-blue-800' },
  delivered: { label: 'Entregue', className: 'bg-green-100 text-green-800' },
  delayed: { label: 'Atrasado', className: 'bg-yellow-100 text-yellow-800' },
  bounced: { label: 'Devolvido', className: 'bg-red-100 text-red-800' },
  complained: { label: 'Marcado como spam', className: 'bg-orange-100 text-orange-800' },
  rejected: { label: 'Rejeitado', className: 'bg-red-100 text-red-800' },
  failed: { label: 'Falha no envio', className: 'bg-red-100 text-red-800' }
};

const TYPE_LABELS = {
  welcome: 'Boas-vindas',
  password_reset: 'Redefinição de senha',
  monitoring: 'Monitoramento',
  other: 'Outro'
};

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-';

const EmailLogs = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  const defaults = getDefaultDates();
  const emptyFilters = {
    startDate: defaults.startDate,
    endDate: defaults.endDate,
    status: '',
    emailType: '',
    organizationId: '',
    search: ''
  };
  // Draft is what the form shows; applied is what was last searched.
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState({ ...emptyFilters, page: 1 });

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: 50 };
      Object.entries(applied).forEach(([key, value]) => {
        if (value !== '') params[key] = value;
      });
      const response = await emailLogs.getAll(params);
      setLogs(response.data.data.logs);
      setPagination(response.data.data.pagination);
    } catch (err) {
      console.error('Error fetching email logs:', err);
      setError('Não foi possível carregar os emails enviados.');
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    if (isSuperAdmin) fetchLogs();
  }, [fetchLogs, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    emailLogs.getStats({ days: 30 })
      .then((response) => setStats(response.data.data))
      .catch((err) => console.error('Error fetching email stats:', err));
    organizations.getAll()
      .then((response) => setOrgs(response.data.data || []))
      .catch((err) => console.error('Error fetching organizations:', err));
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-700">Acesso negado</h2>
          <p className="text-red-600 mt-1">Apenas super administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  const handleChange = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
  const handleSearch = () => setApplied({ ...draft, page: 1 });
  const goToPage = (page) => setApplied((prev) => ({ ...prev, page }));

  const by = stats?.byStatus || {};
  const statCards = [
    { label: 'Enviados (30 dias)', value: stats?.total, icon: Mail, color: 'text-gray-900', iconColor: 'text-brand-500' },
    { label: 'Entregues', value: by.delivered, icon: CheckCircle, color: 'text-green-600', iconColor: 'text-green-500' },
    { label: 'Devolvidos / rejeitados / falhas', value: (by.bounced || 0) + (by.rejected || 0) + (by.failed || 0), icon: XCircle, color: 'text-red-600', iconColor: 'text-red-500' },
    { label: 'Marcados como spam', value: by.complained, icon: AlertTriangle, color: 'text-orange-600', iconColor: 'text-orange-500' }
  ];

  const selectClass = 'w-full border border-gray-300 rounded px-3 py-2 text-sm';

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Emails enviados</h1>
        <p className="text-gray-600 text-sm mt-1">
          Status de entrega dos emails enviados pelo GiA. Apenas metadados são armazenados — nunca o assunto ou o conteúdo. Registros são mantidos por 12 meses.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {statCards.map(({ label, value, icon: Icon, color, iconColor }) => (
            <div key={label} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className={`text-2xl font-bold ${color}`}>{(value || 0).toLocaleString('pt-BR')}</p>
                </div>
                <Icon className={`h-8 w-8 ${iconColor}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1" />
              Data Início
            </label>
            <input type="date" value={draft.startDate} onChange={(e) => handleChange('startDate', e.target.value)} className={selectClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1" />
              Data Fim
            </label>
            <input type="date" value={draft.endDate} onChange={(e) => handleChange('endDate', e.target.value)} className={selectClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Filter className="inline h-4 w-4 mr-1" />
              Status
            </label>
            <select value={draft.status} onChange={(e) => handleChange('status', e.target.value)} className={selectClass}>
              <option value="">Todos</option>
              {Object.entries(STATUS).map(([value, { label }]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select value={draft.emailType} onChange={(e) => handleChange('emailType', e.target.value)} className={selectClass}>
              <option value="">Todos</option>
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Building2 className="inline h-4 w-4 mr-1" />
              Organização
            </label>
            <select value={draft.organizationId} onChange={(e) => handleChange('organizationId', e.target.value)} className={selectClass}>
              <option value="">Todas</option>
              <option value="none">Sistema (sem organização)</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Search className="inline h-4 w-4 mr-1" />
              Destinatário
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={draft.search}
                onChange={(e) => handleChange('search', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Buscar por email..."
                className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <button onClick={handleSearch} className="px-4 py-2 bg-brand-700 text-white rounded hover:bg-brand-800 text-sm">
                Buscar
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organização</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatário</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalhe</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">Carregando...</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">Nenhum email encontrado</td>
                </tr>
              ) : (
                logs.map((log) => {
                  const status = STATUS[log.status] || { label: log.status, className: 'bg-gray-100 text-gray-800' };
                  return (
                    <tr key={log.id} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(log.sentAt)}
                        {log.lastEventAt && (
                          <div className="text-xs text-gray-500">Última atualização: {formatDateTime(log.lastEventAt)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {log.organization?.name || <span className="text-gray-500">Sistema</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {TYPE_LABELS[log.emailType] || log.emailType}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{log.recipient}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>{status.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-xs break-words">{log.statusDetail || '-'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t">
            <div className="text-sm text-gray-700">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} até{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
              {pagination.total} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">Página {pagination.page} de {pagination.totalPages}</span>
              <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      <VersionFooter />
    </div>
  );
};

export default EmailLogs;
